import { INewsPage } from "@/cms-models/news";
import { NewsGridSection } from "@/components/news";
import { imageUrl } from "@/utils";
import { dateFormater } from "@/utils/dataConverter";
import axios from "axios";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { NextRouter, useRouter } from "next/router";
import { useEffect, useState } from "react";
import { CiClock2 } from "react-icons/ci";
import { useRouter as useRtRouter } from "next/router";
import styles from '@/styles/styles.module.css'
import { useTranslations } from "next-intl";

export default function NewsDetail() {
  const router: NextRouter = useRouter();
  const rt = useRtRouter()
  const { id } = rt.query;
  const [data, setData] = useState<INewsPage>({} as INewsPage);
  const [relevantBlogs, setRelevantData] = useState<INewsPage[]>([]);
  const t = useTranslations("News");

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  useEffect(() => {
    if (!router.isReady) return;

    (async () => {
      let currentNewsId: number | null = null;

      if (id) {
        try {
          const response = await axios(
            `/${router.locale}/news/${id}`
          );
          if (response.status === 200) {
            setData(response.data);
            currentNewsId = response.data.id;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catch (error) {
          rt.push(`/${router.locale}/news`);
          return;
        }
      }

      try {
        const response = await axios(`/${router.locale}/news`);
        if (response.status === 200) {
          if (response.data && response.data.length > 0) {
            const blogs: INewsPage[] = response.data;
            const routeId = Number(Array.isArray(id) ? id[0] : id);
            const filteredBlogs = blogs.filter(
              (blog) =>
                (currentNewsId === null || blog.id !== currentNewsId) &&
                (Number.isNaN(routeId) || blog.id !== routeId)
            );

            const sortedData: INewsPage[] =
              filteredBlogs && filteredBlogs.length > 0
                ? filteredBlogs
                  .sort(
                    (a, b) =>
                      new Date(b.date).getTime() - new Date(a.date).getTime()
                  )
                  .slice(0, 3)
                : [];
            setRelevantData(sortedData);
          } else setRelevantData(response.data);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) { }
    })();
  }, [router.locale, router.query, router.isReady, router, id]);

  if (!data || Object.keys(data).length === 0) {
    return <div>Loading...</div>;
  }

  const images = data.image ? JSON.parse(data.image) : "";

  return (
    <div className="container mx-auto">
      <Head>
        <title>{`${stripHtml(data.title)}`}</title>
        <meta
          name="description"
          content="Read all the latest news and blog posts from Premier Vet Alliance, and keep up to date with the latest developments."
        ></meta>
        <link
          rel="canonical"
          href="https://premiervetalliance.com/practice-support/"
        />
        <meta property="og:locale" content="en_US"></meta>
        <meta property="og:type" content="article"></meta>
        <meta
          property="og:title"
          content="5 Things You Need To Do In Your Practice!"
        ></meta>
        <meta
          property="og:description"
          content="Read all the latest news and blog posts from Premier Vet Alliance, and keep up to date with the latest developments."
        ></meta>
        <meta
          property="og:url"
          content="https://premiervetalliance.com/practice-support/"
        ></meta>
        <meta property="og:site_name" content="Premier Vet Alliance"></meta>
      </Head>
      <section className="max-w-[846px] mx-auto text-base md:text-xl pt-6">
        <div className="flex justify-between md:items-center flex-col md:flex-row gap-6 mb-3 md:mb-0">
          <div className="text-secondary body flex gap-1">
            <Link href="/news">News </Link>/{" "}
            <span className="text-primary" dangerouslySetInnerHTML={{ __html: data.title }}>
            </span>
          </div>

          <div className="flex items-center text-secondary gap-1">
            <CiClock2 className="text-sm md:text-base sm:text-xl" />
            <div className="flex flex-col text-xs sm:text-base">
              <span>{dateFormater(data.date, "MMM DD, YYYY")}</span>
              {data.read_time && <span>{data.read_time}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 md:gap-10">
          <div
            className='sub-heading'
            dangerouslySetInnerHTML={{ __html: data.title }}
          />

          <Image
            className="w-full"
            src={imageUrl(images?.[0] ?? "")}
            alt="news-detail-top"
            width={846}
            height={500}
          />

          <div className={styles.safe_html + " content-html"} dangerouslySetInnerHTML={{ __html: data.description }} />
        </div>
      </section>

      <section className="pt-20">
        <div className="sub-heading mb-12" dangerouslySetInnerHTML={{ __html: t.raw("you_might_also_like") }} />
        <div className="">
          <NewsGridSection data={relevantBlogs} />
        </div>
      </section>

      <section className="-mx-10">{/*<NeedMoreHelpSection />*/}</section>
    </div>
  );
}
